import { useRef, useState } from 'react';
import { useReglages } from '../../api/reglages.jsx';
import { iaApi } from '../../api/resources';
import { Btn } from '../../components/ui/Btn.jsx';
import { prepareFichiers } from '../../lib/document-ia';
import { messageIa } from '../../lib/ia';
import { CLE_PACK_IA, packIaActif } from '../../lib/pack-ia';
import { verifieFichiers } from '../../lib/soumission';
import { C } from '../../theme/palette';

const champ = {
  border: '1px solid ' + C.border,
  borderRadius: 7,
  padding: '6px 7px',
  fontSize: 12,
  boxSizing: 'border-box',
  width: '100%',
};

/** Un article proposé, rendu modifiable par l'écran de vérification. */
const versLigne = (a, i) => ({
  cle: i,
  garde: true,
  existantId: a.existantId ?? null,
  existantNom: a.existantNom || '',
  correspondance: a.correspondance || '',
  nom: a.nom || '',
  unite: a.unite || '',
  prix: a.prix ?? '',
  codeBarre: a.codeBarre || '',
  categorie: a.categorie || '',
  referenceFournisseur: a.referenceFournisseur || '',
  image: a.image || null,
});

/**
 * « 📥 Importer un catalogue » : le PDF ou les photos du catalogue d'un
 * fournisseur deviennent des articles du stock, après vérification.
 *
 * Rien n'est créé avant la validation de l'écran de vérification. Un article
 * déjà en stock — même code-barres, sinon même nom — est mis à jour, jamais
 * doublé ; un prix absent laisse l'article à compléter. Sans le Pack IA, le
 * bouton n'existe pas.
 *
 * @param {object} props
 * @param {() => void} [props.onImporte] appelé après l'import, pour relire le stock
 * @returns {import('react').ReactElement|null} le bouton et son écran, ou rien
 */
export function ImportCatalogue({ onImporte }) {
  const { reglageJson } = useReglages();
  const actif = packIaActif(reglageJson(CLE_PACK_IA, null));
  const [ouvert, setOuvert] = useState(false);
  const [fichiers, setFichiers] = useState([]);
  const [etat, setEtat] = useState('');
  const [lu, setLu] = useState(null);
  const [lignes, setLignes] = useState([]);
  const [prixSont, setPrixSont] = useState('achat');
  const [resultat, setResultat] = useState(null);
  const entreePdf = useRef(null);
  const entreePhotos = useRef(null);
  if (!actif) return null;

  const ferme = () => {
    setOuvert(false);
    setFichiers([]);
    setLu(null);
    setLignes([]);
    setEtat('');
  };

  const lit = async () => {
    const erreur = verifieFichiers(fichiers, { texteAdmis: false });
    if (erreur) {
      setEtat(erreur);
      return;
    }
    setEtat('lecture');
    try {
      const prets = await prepareFichiers(fichiers);
      const reponse = await iaApi.lectureCatalogue({ fichiers: prets });
      setLu(reponse);
      setLignes((reponse.articles || []).map(versLigne));
      setEtat('');
    } catch (e) {
      setEtat(e?.status != null ? messageIa(e) : e?.message || messageIa(e));
    }
  };

  const importe = async () => {
    const gardees = lignes.filter((l) => l.garde && l.nom.trim());
    if (!gardees.length) return;
    setEtat('import');
    try {
      const r = await iaApi.importeCatalogue({
        fournisseur: lu?.fournisseur || null,
        prixSont,
        articles: gardees.map((l) => ({
          existantId: l.existantId,
          nom: l.nom.trim(),
          unite: l.unite || null,
          prix: l.prix === '' || l.prix == null ? null : +l.prix,
          codeBarre: l.codeBarre || null,
          categorie: l.categorie || null,
          referenceFournisseur: l.referenceFournisseur || null,
          image: l.image,
        })),
      });
      setResultat(r);
      ferme();
      onImporte && onImporte();
    } catch (e) {
      setEtat(messageIa(e));
    }
  };

  const maj = (cle, champNom, valeur) =>
    setLignes((p) => p.map((l) => (l.cle === cle ? { ...l, [champNom]: valeur } : l)));
  const gardees = lignes.filter((l) => l.garde);
  const nouveaux = gardees.filter((l) => !l.existantId).length;

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setResultat(null);
          setOuvert(true);
        }}
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 7,
          background: C.navyXL,
          border: '1px dashed ' + C.navy + '55',
          borderRadius: 11,
          padding: 12,
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 800,
          color: C.navy,
          marginBottom: 9,
        }}
      >
        📥 Importer un catalogue{' '}
        <span style={{ fontSize: 9, fontWeight: 600, opacity: 0.7 }}>(scan / PDF · Pack IA)</span>
      </button>
      {resultat && (
        <div
          role="status"
          style={{
            fontSize: 10.5,
            color: C.green,
            background: C.greenL,
            borderRadius: 9,
            padding: 9,
            marginBottom: 9,
          }}
        >
          {resultat.crees} article(s) créé(s), {resultat.misAJour} mis à jour
          {resultat.images ? `, ${resultat.images} image(s) jointe(s)` : ''}.
          {resultat.sansPrix?.length > 0 && ` Prix à compléter : ${resultat.sansPrix.join(', ')}.`}
        </div>
      )}
      {ouvert && (
        <div
          role="dialog"
          aria-label="Importer un catalogue"
          style={{
            position: 'fixed',
            inset: 0,
            paddingTop: 'var(--st-haut)',
            zIndex: 9999,
            background: '#fff',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <div style={{ maxWidth: 680, margin: '0 auto', padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <b style={{ fontSize: 15, color: C.ink }}>📥 Importer un catalogue fournisseur</b>
              <Btn label="Fermer" sm ghost color={C.grey} onClick={ferme} />
            </div>

            {!lu && (
              <>
                <div style={{ fontSize: 11, color: C.grey, lineHeight: 1.6, marginBottom: 10 }}>
                  Importe le PDF ou des photos du catalogue (avec ou sans prix). Les articles te
                  sont proposés avant toute création : tu vérifies, puis tu valides.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <Btn
                    label="📄 PDF"
                    color={C.navy}
                    full
                    onClick={() => entreePdf.current?.click()}
                  />
                  <Btn
                    label="📷 Photos / scan"
                    color={C.navy}
                    full
                    onClick={() => entreePhotos.current?.click()}
                  />
                </div>
                <input
                  ref={entreePdf}
                  type="file"
                  accept="application/pdf,.pdf"
                  aria-label="Catalogue en PDF"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    setFichiers(Array.from(e.target.files || []));
                    e.target.value = '';
                  }}
                />
                <input
                  ref={entreePhotos}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  aria-label="Pages du catalogue photographiées"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    setFichiers(Array.from(e.target.files || []));
                    e.target.value = '';
                  }}
                />
                {fichiers.length > 0 && (
                  <div style={{ fontSize: 10.5, color: C.grey, margin: '9px 0' }}>
                    {fichiers.map((f) => f.name).join(', ')}
                  </div>
                )}
                <div style={{ marginTop: 10 }}>
                  <Btn
                    label={
                      etat === 'lecture'
                        ? '⏳ Lecture en cours… (jusqu’à quelques minutes)'
                        : '🔍 Lire le catalogue'
                    }
                    color={C.teal}
                    full
                    disabled={etat === 'lecture' || !fichiers.length}
                    onClick={lit}
                  />
                </div>
              </>
            )}

            {lu && (
              <>
                <div style={{ fontSize: 11, color: C.grey, lineHeight: 1.6, marginBottom: 8 }}>
                  <b>{lignes.length} article(s) lu(s)</b>
                  {lu.fournisseur ? ' chez ' + lu.fournisseur : ''}. Décoche ce qui ne doit pas
                  entrer au stock, corrigez au besoin.
                  {lu.remarque && <div style={{ fontSize: 10, color: C.muted }}>{lu.remarque}</div>}
                </div>
                <div
                  role="radiogroup"
                  aria-label="Nature des prix"
                  style={{ fontSize: 11, marginBottom: 9 }}
                >
                  Les prix du catalogue sont mes{' '}
                  <label>
                    <input
                      type="radio"
                      name="prix-catalogue"
                      checked={prixSont === 'achat'}
                      onChange={() => setPrixSont('achat')}
                    />{' '}
                    prix d'achat
                  </label>{' '}
                  <label>
                    <input
                      type="radio"
                      name="prix-catalogue"
                      checked={prixSont === 'vente'}
                      onChange={() => setPrixSont('vente')}
                    />{' '}
                    prix de vente
                  </label>
                </div>
                {lignes.map((l) => (
                  <div
                    key={l.cle}
                    data-testid="article-catalogue"
                    style={{
                      border: '1px solid ' + C.border,
                      borderRadius: 9,
                      padding: 9,
                      marginBottom: 7,
                      opacity: l.garde ? 1 : 0.5,
                    }}
                  >
                    <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 5 }}>
                      <input
                        type="checkbox"
                        checked={l.garde}
                        aria-label={'Importer ' + l.nom}
                        onChange={() => maj(l.cle, 'garde', !l.garde)}
                      />
                      {l.image && (
                        <img
                          src={'data:' + l.image.typeMedia + ';base64,' + l.image.contenu}
                          alt=""
                          style={{ width: 38, height: 38, objectFit: 'cover', borderRadius: 6 }}
                        />
                      )}
                      <input
                        value={l.nom}
                        aria-label="Nom de l'article"
                        onChange={(e) => maj(l.cle, 'nom', e.target.value)}
                        style={{ ...champ, fontWeight: 700 }}
                      />
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '0.7fr 1fr 1.3fr 1.2fr',
                        gap: 5,
                      }}
                    >
                      <input
                        value={l.unite}
                        placeholder="Unité"
                        aria-label="Unité"
                        onChange={(e) => maj(l.cle, 'unite', e.target.value)}
                        style={champ}
                      />
                      <input
                        value={l.prix}
                        placeholder="Prix à compléter"
                        inputMode="decimal"
                        aria-label={'Prix ' + l.nom}
                        onChange={(e) => maj(l.cle, 'prix', e.target.value.replace(',', '.'))}
                        style={{ ...champ, borderColor: l.prix === '' ? C.amber : C.border }}
                      />
                      <input
                        value={l.codeBarre}
                        placeholder="Code-barres"
                        inputMode="numeric"
                        aria-label="Code-barres"
                        onChange={(e) => maj(l.cle, 'codeBarre', e.target.value)}
                        style={champ}
                      />
                      <input
                        value={l.categorie}
                        placeholder="Catégorie"
                        aria-label="Catégorie"
                        onChange={(e) => maj(l.cle, 'categorie', e.target.value)}
                        style={champ}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: 9.5,
                        marginTop: 4,
                        color: l.existantId ? C.teal : C.green,
                      }}
                    >
                      {l.existantId
                        ? `↻ Mise à jour de « ${l.existantNom} » (même ${
                            l.correspondance === 'code_barre' ? 'code-barres' : 'nom'
                          })`
                        : '＋ Nouvel article'}
                    </div>
                  </div>
                ))}
                <Btn
                  label={
                    etat === 'import'
                      ? '⏳ Import en cours…'
                      : `✓ Créer ${nouveaux} article(s) et mettre à jour ${gardees.length - nouveaux}`
                  }
                  color={C.green}
                  full
                  disabled={etat === 'import' || !gardees.length}
                  onClick={importe}
                />
              </>
            )}
            {etat && etat !== 'lecture' && etat !== 'import' && (
              <div role="alert" style={{ fontSize: 10.5, color: C.red, marginTop: 8 }}>
                {etat}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
